import { useState } from 'react';
import Router from 'next/router';
import { hostedAppPath } from '../../../lib/app-open-target';
import { offerTitle } from '../../../lib/hosted-app-status';
import { useTranslation } from '../../../lib/i18n';
import { answerAppShare, type WorkspaceApp } from '../../../lib/app-shares';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { Button, Card, Typography } from '../ui';
import styles from './PendingShares.module.css';

/**
 * Apps somebody OFFERED this person, waiting for an answer.
 *
 * <p><b>What the reader is told before they answer.</b> The app's name and what it is for. When the
 * offer lends tools, the count and the connections are named too — a person cannot weigh "this app can
 * act on someone else's account" without knowing how much of it, and "access to their connections"
 * would describe forty tools as readily as two.
 *
 * <p><b>Both answers are final in opposite directions, so neither is the default.</b> Accepting takes
 * the app; declining DELETES the share, exactly as the owner's revoke would — the owner can offer
 * again, and that is a fresh offer. Nothing here is pre-selected and nothing happens on a stray click:
 * each row's two buttons are the only way through.
 */
export function PendingShares({
  offers,
  onAnswered,
  onBi,
}: {
  offers: WorkspaceApp[];
  onAnswered?: () => void;
  onBi?: (buttonName: string) => void;
}) {
  const { t } = useTranslation();
  // Keyed by app id rather than a single flag: two offers can be answered in quick succession, and one
  // shared flag would grey out a row the reader has not touched.
  const [busy, setBusy] = useState<Record<string, 'accept' | 'decline' | undefined>>({});
  const [failed, setFailed] = useState<Record<string, boolean>>({});

  if (offers.length === 0) {
    return null;
  }

  const answer = async (app: WorkspaceApp, choice: 'accept' | 'decline') => {
    if (busy[app.id]) return;
    onBi?.(choice === 'accept' ? 'accept_app_share' : 'decline_app_share');
    setBusy((prev) => ({ ...prev, [app.id]: choice }));
    setFailed((prev) => ({ ...prev, [app.id]: false }));
    try {
      await answerAppShare(app.id, choice);
      // Both lists come from one read, so one invalidation moves the app out of here and — when
      // accepted — into the shared list, with no window where it is in neither or both.
      await queryClient.invalidateQueries({ queryKey: queryKeys.sharedApps() });
      onAnswered?.();
      // Accepting a HOSTED app opens it: the reader just said yes to using it, and it lives in Symphony.
      if (choice === 'accept' && app.hosted) void Router.push(hostedAppPath(app.id));
    } catch {
      // The row stays, saying so. Removing it on a failed answer would look like the answer landed.
      setFailed((prev) => ({ ...prev, [app.id]: true }));
    } finally {
      setBusy((prev) => ({ ...prev, [app.id]: undefined }));
    }
  };

  return (
    <>
      <Typography variant="h3" component="h2">
        {t('apps.pendingShares.title')}
      </Typography>
      <div className={styles.list}>
        {offers.map((app) => (
          <Card key={app.id} className={styles.row}>
            <div className={styles.text}>
              {/* The offer names who made it: an app from a stranger and one from your partner are different decisions. */}
              <Typography variant="h4" color="strong" component="h3">
                {t(offerTitle(app.name, app.ownerName).key, offerTitle(app.name, app.ownerName).params)}
              </Typography>
              {app.description && (
                <Typography variant="p3" color="secondary" component="p">
                  {app.description}
                </Typography>
              )}
              <Typography variant="p3" color="secondary" component="p">
                {t('apps.pendingShares.hint')}
              </Typography>
              {failed[app.id] && (
                <Typography variant="p3" component="p" className={styles.failed}>
                  {t('apps.pendingShares.answerFailed')}
                </Typography>
              )}
            </div>
            <div className={styles.actions}>
              <Button
                size="small"
                loading={busy[app.id] === 'accept'}
                disabled={Boolean(busy[app.id])}
                onClick={() => void answer(app, 'accept')}
              >
                {t('apps.pendingShares.accept')}
              </Button>
              {/* Not destructive-red. Turning down an offer is an ordinary answer, and painting it as
                  damage pushes people toward accepting things they have not read. */}
              <Button
                size="small"
                variant="outlined"
                loading={busy[app.id] === 'decline'}
                disabled={Boolean(busy[app.id])}
                onClick={() => void answer(app, 'decline')}
              >
                {t('apps.pendingShares.decline')}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
